import { useEffect, useState } from 'react';
import { createLeadAppointment, getCompanyServices, sendLeadAppointmentConfirmation } from '../services/api';
import { track } from '../services/analytics';
import { appointmentHelp } from '../utils/helpContent';
import HelpTooltip from './HelpTooltip';
import ServiceManager from './ServiceManager';

const AppointmentForm = ({ 
  authToken, 
  leadId, 
  onAppointmentCreated, 
  onClose,
  compact = false 
}) => {
  const [formData, setFormData] = useState({
    date_time: '',
    type: '',
    status: 'scheduled',
    duration: '01:00:00'
  });
  
  // Separate date and time states for better UX
  const [appointmentDate, setAppointmentDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  });
  const [appointmentTime, setAppointmentTime] = useState('09:00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [services, setServices] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  
  // Service management modal state
  const [showServiceManager, setShowServiceManager] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Services will be fetched from API instead of hardcoded types

  const durations = [
    { value: '00:30:00', label: '30 minutes' },
    { value: '01:00:00', label: '1 hour' },
    { value: '01:30:00', label: '1.5 hours' },
    { value: '02:00:00', label: '2 hours' },
    { value: '02:30:00', label: '2.5 hours' },
    { value: '03:00:00', label: '3 hours' }
  ];

  useEffect(() => {
    fetchServices();
  }, [leadId, authToken]);


  const fetchServices = async () => {
    try {
      const response = await getCompanyServices(authToken);
      // Handle paginated response from DRF like in ServiceSelector
      let serviceData;
      if (response && response.results && Array.isArray(response.results)) {
        serviceData = response.results;
      } else if (Array.isArray(response)) {
        serviceData = response;
      } else {
        console.error('Services response has unexpected format:', response);
        serviceData = [];
      }
      setServices(serviceData);
    } catch (err) {
      console.error('Error fetching services:', err);
      setServices([]);
    }
  };

  const handleCreateService = () => {
    setEditingService(null);
    setShowServiceManager(true);
    setShowServiceDropdown(false);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowServiceManager(true);
    setShowServiceDropdown(false);
  };

  const handleServiceSaved = async (savedService) => {
    // Refresh the available services list
    try {
      const response = await getCompanyServices(authToken);
      let serviceData;
      if (response && response.results && Array.isArray(response.results)) {
        serviceData = response.results;
      } else if (Array.isArray(response)) {
        serviceData = response;
      } else {
        serviceData = [];
      }
      setServices(serviceData);
      
      // If this was a new service, auto-select it as the appointment type
      if (!editingService) {
        setFormData(prev => ({
          ...prev,
          type: savedService.name.toLowerCase().replace(/\s+/g, '_')
        }));
      }
    } catch (err) {
      console.error('Error refreshing services:', err);
    }
  };

  const handleCloseServiceManager = () => {
    setShowServiceManager(false);
    setEditingService(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Combine date and time for submission (keep as local time)
      const dateTime = `${appointmentDate}T${appointmentTime}:00`;
      
      // Create appointment
      const appointmentData = {
        ...formData,
        date_time: dateTime
      };
      
      const newAppointment = await createLeadAppointment(authToken, leadId, appointmentData);

      track('appointment_created', {
        entity_type: 'lead',
        service_count: formData.type ? 1 : 0,
        days_out: Math.round(
          (new Date(dateTime) - Date.now()) / 86400000
        ),
      });
      
      // Send confirmation if requested
      if (sendConfirmation) {
        try {
          await sendLeadAppointmentConfirmation(authToken, leadId, newAppointment.id);
        } catch (confirmError) {
          console.error('Failed to send confirmation:', confirmError);
          // Don't fail the whole process if confirmation fails
        }
      }

      setSuccess(true);
      if (onAppointmentCreated) {
        onAppointmentCreated(newAppointment);
      }
      
      // Reset form
      setFormData({
        date_time: '',
        type: '',
        status: 'scheduled',
        duration: '01:00:00'
      });
      
      // Reset date/time to defaults
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setAppointmentDate(tomorrow.toISOString().split('T')[0]);
      setAppointmentTime('09:00');

      // Close form after short delay if in compact mode
      if (compact) {
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
      
    } catch (err) {
      console.error('Error creating appointment:', err);
      setError(err.response?.data?.message || 'Failed to schedule appointment. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  if (success && compact) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg className="h-5 w-5 text-green-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-sm font-medium text-green-800">
            Appointment scheduled successfully!
            {sendConfirmation && ' Confirmation sent.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={compact ? "space-y-4" : "bg-white rounded-lg shadow-sm p-6"}
    >
      {!compact && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Schedule Appointment
          </h3>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {success && !compact && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-green-400 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="text-sm font-medium text-green-800">
              Appointment scheduled successfully!
              {sendConfirmation && " Confirmation sent to lead."}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 text-red-400 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Date and Time */}
        <div>
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            Date & Time *
            <HelpTooltip
              content="Select the appointment date and time. Only future dates are allowed."
              className="ml-1"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Date Input */}
            <div>
              <label
                htmlFor="appointment_date"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Date
              </label>
              <input
                type="date"
                id="appointment_date"
                value={appointmentDate}
                onChange={(e) => setAppointmentDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                required
                autoFocus
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-text-primary focus:border-text-primary"
              />
            </div>

            {/* Time Input with Quick Buttons */}
            <div>
              <label
                htmlFor="appointment_time"
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Time
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="time"
                  id="appointment_time"
                  value={appointmentTime}
                  onChange={(e) => setAppointmentTime(e.target.value)}
                  required
                  className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-text-primary focus:border-text-primary"
                />
              </div>

              {/* Quick Time Buttons */}
              <div className="flex flex-wrap gap-1 mt-2">
                {[
                  "08:00",
                  "09:00",
                  "10:00",
                  "11:00",
                  "13:00",
                  "14:00",
                  "15:00",
                  "16:00",
                ].map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setAppointmentTime(time)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      appointmentTime === time
                        ? "bg-accent-teal-subtle border-text-primary text-slate-600"
                        : "bg-background border-border text-gray-600 hover:bg-accent-teal-subtle"
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Type */}
        <div>
          <label
            htmlFor="type"
            className="flex items-center text-sm font-medium text-gray-700 mb-1"
          >
            Type *
            <HelpTooltip
              title={appointmentHelp.appointmentTypes.title}
              content={appointmentHelp.appointmentTypes.content}
              className="ml-1"
              size="md"
            />
          </label>
          {services.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {services.slice(0, 6).map((service) => {
                const typeKey = service.name.toLowerCase().replace(/\s+/g, "_");
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, type: typeKey }))
                    }
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      formData.type === typeKey
                        ? "bg-accent-teal-subtle border-text-primary text-slate-600"
                        : "bg-background border-border text-gray-600 hover:bg-accent-teal-subtle"
                    }`}
                  >
                    {service.name}
                  </button>
                );
              })}
              {services.length > 6 && (
                <button
                  type="button"
                  onClick={() => setShowServiceDropdown(true)}
                  className="px-2 py-1 text-xs rounded border border-border text-gray-500 hover:bg-background transition-colors"
                >
                  More...
                </button>
              )}
            </div>
          )}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowServiceDropdown(!showServiceDropdown)}
              className="w-full bg-white border border-border rounded-md px-3 py-2 text-left focus:ring-2 focus:ring-text-primary focus:border-text-primary transition duration-300"
            >
              <div className="flex items-center justify-between">
                <span
                  className={formData.type ? "text-gray-900" : "text-gray-500"}
                >
                  {formData.type
                    ? services.find(
                        (s) =>
                          s.name.toLowerCase().replace(/\s+/g, "_") ===
                          formData.type,
                      )?.name || formData.type
                    : "Select appointment type"}
                </span>
                <svg
                  className={`h-5 w-5 text-gray-400 transform transition-transform ${
                    showServiceDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            {showServiceDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-lg shadow-sm max-h-60 overflow-y-auto">
                {/* Add New Service Button */}
                <div className="p-2 border-b border-border">
                  <button
                    type="button"
                    onClick={handleCreateService}
                    className="w-full flex items-center justify-center px-3 py-2 bg-accent-teal-light-bg text-primary rounded-lg hover:bg-accent-teal-subtle transition-colors"
                  >
                    <svg
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add New Service
                  </button>
                </div>

                {services.map((service) => (
                  <div
                    key={service.id}
                    className="flex items-center px-2 py-2 hover:bg-background rounded group"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          type: service.name.toLowerCase().replace(/\s+/g, "_"),
                        }));
                        setShowServiceDropdown(false);
                      }}
                      className="flex-1 text-left px-2 py-1"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-gray-900 font-medium">
                          {service.name}
                        </span>
                      </div>
                      {service.description && (
                        <p className="text-gray-600 text-sm mt-1">
                          {service.description}
                        </p>
                      )}
                    </button>
                    {/* Edit Button */}
                    <button
                      type="button"
                      onClick={() => handleEditService(service)}
                      className="ml-2 p-1 text-gray-400 hover:text-primary opacity-0 group-hover:opacity-100 transition-all"
                      title="Edit service"
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Duration */}
        <div>
          <label
            htmlFor="duration"
            className="flex items-center text-sm font-medium text-gray-700 mb-1"
          >
            Duration
            <HelpTooltip
              title={appointmentHelp.appointmentDuration.title}
              content={appointmentHelp.appointmentDuration.content}
              className="ml-1"
            />
          </label>
          <select
            id="duration"
            name="duration"
            value={formData.duration}
            onChange={handleChange}
            className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-text-primary focus:border-text-primary"
          >
            {durations.map((duration) => (
              <option key={duration.value} value={duration.value}>
                {duration.label}
              </option>
            ))}
          </select>
        </div>

        {/* Send Confirmation */}
        <div className="flex items-center">
          <input
            id="send_confirmation"
            type="checkbox"
            checked={sendConfirmation}
            onChange={(e) => setSendConfirmation(e.target.checked)}
            className="h-4 w-4 text-primary focus:ring-text-primary border-border rounded"
          />
          <label
            htmlFor="send_confirmation"
            className="ml-2 flex items-center text-sm text-gray-700"
          >
            Send confirmation SMS/email to lead
            <HelpTooltip
              title={appointmentHelp.sendConfirmation.title}
              content={appointmentHelp.sendConfirmation.content}
              className="ml-1"
              size="md"
            />
          </label>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3 pt-4">
          {onClose && !compact && (
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-background focus:ring-2 focus:ring-text-primary"
            >
              Cancel
            </button>
          )}
          <button
            data-attr="lead-create-appointment"
            type="submit"
            disabled={loading}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary hover:bg-secondary/90/85 focus:ring-2 focus:ring-text-primary disabled:opacity-50 disabled:cursor-not-allowed ${
              loading ? "cursor-not-allowed" : ""
            }`}
          >
            {loading ? "Scheduling..." : "Schedule Appointment"}
          </button>
        </div>
      </form>

      {/* Close dropdown on outside click */}
      {showServiceDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowServiceDropdown(false)}
        />
      )}

      {/* Service Manager Modal */}
      <ServiceManager
        authToken={authToken}
        isOpen={showServiceManager}
        onClose={handleCloseServiceManager}
        onServiceSaved={handleServiceSaved}
        editingService={editingService}
        hidePricing={true}
      />
    </div>
  );
};

export default AppointmentForm;